import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Loader2, Mail, User, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.subject || !formData.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: '',
          website: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Mail className="w-16 h-16 mx-auto mb-6 text-orange-600" />
              <h1 className="text-5xl font-bold mb-4 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
                Nous contacter
              </h1>
              <p className="text-slate-600 text-lg">
                Une question, une proposition de partenariat ou d'événement ? Écrivez-nous.
              </p>
            </div>

            <Card className="border-2 border-slate-200 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    <label htmlFor="website">Ne pas remplir ce champ</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Jean" className="pl-10" required data-testid="contact-firstname" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Dupont" className="pl-10" required data-testid="contact-lastname" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="jean.dupont@example.com" className="pl-10" required data-testid="contact-email" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Objet *</Label>
                    <Select value={formData.subject} onValueChange={(v) => setFormData({...formData, subject: v})}>
                      <SelectTrigger data-testid="contact-subject">
                        <SelectValue placeholder="Choisissez un objet" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Question générale">Question générale</SelectItem>
                        <SelectItem value="Adhésion">Adhésion</SelectItem>
                        <SelectItem value="Proposition de partenariat">Proposition de partenariat</SelectItem>
                        <SelectItem value="Proposition d'événement">Proposition d'événement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Votre message..." rows={6} className="pl-10" required data-testid="contact-message" />
                    </div>
                  </div>

                  <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white text-lg py-6" data-testid="contact-submit">
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Envoi en cours...</>
                    ) : (
                      <>Envoyer le message<Send className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
